/* eslint-disable prettier/prettier */
import { Box, Divider, Grid, useMediaQuery } from "@mui/material";
import { Formik } from "formik";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { css, StyleSheet } from "aphrodite";
import { Helmet } from "react-helmet";
import Button from "../../../../components/common/Button";
import OrganizationStructure from "../../../../components/common/OrganizationStructure/OrganizationStructure";
import OrganizationStructureRegion from "../../../../components/common/OrganizationStructure/OrganizationStructureRegion";
import Toast from "../../../../components/common/Toast";
import { NEW_FRIEND_FIELDS } from "../../../../contants/formFields";
import { selectUserProfile } from "../../../../redux/selectors/user";
import { selectAttendanceMeetingType } from "../../../../redux/selectors/attendance";
import FriendsApi from "../../../../services/api/friend";
import { handleFriendsValidation } from "../../../../utils";
import { debounce } from "../../../../utils/helper";
import NewFriendForm from "../NewFriendEntry";
import { OS_KEYS } from "../../../../contants";

function FriendForm() {
  const TITLE = "Create New Friend - BSG";
  const [showToast, setShowToast] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const user = useSelector(selectUserProfile); // can be undefined on refresh
  const safeUser = user || {}; // ✅ prevents crash
  const onBack = () => navigate(-1);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const meetingType = useSelector(selectAttendanceMeetingType);
  const positionRole = localStorage.getItem("positionRole");
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);

  // ✅ stable initial values (and safeUser guarded)
  const initialOrganizationValue = useMemo(() => {
    const initialFormValue = {};

    if (safeUser.regionSi) {
      initialFormValue.areaId = safeUser.areaId;
      initialFormValue.clusterId = safeUser.clusterId;
      initialFormValue.zoneId = safeUser.zoneId;
      initialFormValue.regionId = safeUser.regionId;
    }

    if (safeUser.chapterSi || safeUser.districtSi) {
      initialFormValue.areaId = safeUser.areaId;
      initialFormValue.clusterId = safeUser.clusterId;
      initialFormValue.zoneId = safeUser.zoneId;
      initialFormValue.regionId = safeUser.regionId;
      initialFormValue.chapterId = safeUser.chapterId;
    }

    const initialOrg = { ...initialFormValue };

    if (safeUser.districtSi) {
      initialOrg.districtId = safeUser.districtId;
    }
    if (safeUser.regionSi || safeUser.districtSi) {
      initialOrg.regionId = safeUser.regionId;
    }

    return initialOrg;
  }, [
    safeUser.areaId,
    safeUser.clusterId,
    safeUser.zoneId,
    safeUser.regionId,
    safeUser.chapterId,
    safeUser.districtId,
    safeUser.chapterSi,
    safeUser.districtSi,
  ]);

  const postData = async (values) => {
    // ✅ prevent multiple submits
    if (isLoading || isSubmitLocked) return;

    try {
      setIsLoading(true);

      const isGoshoOrDiscussion =
        meetingType === "GOSHO_MEETING" || meetingType === "DISCUSSION_MEETING";

      // ✅ FIX: Region SI meeting types ko bhi skip karo
      const REGION_MEETING_TYPES = new Set([
        "CUBS_MEETING",
        "YP_MEETING",
        "YL_MEETING",
        "SD_MEETING",
      ]);

      const shouldSkipOtherMeetingType =
        isGoshoOrDiscussion || REGION_MEETING_TYPES.has(meetingType);

      const payload = {
        ...values,
        meetingType: `${meetingType}`,
        ...(!shouldSkipOtherMeetingType
          ? { otherMeetingType: [meetingType] }
          : {}),
      };

      const response = await FriendsApi.postNewFriend({ data: payload });

      setTimeout(() => {
        navigate("/attendance");
      }, 2000);

      if (response) {
        setIsSubmitLocked(true);

        setShowToast({
          message: "Form submitted successfully",
          severity: "success",
        });

        setTimeout(() => {
          navigate("/attendance"); // ✅ ya navigate(-2) (but one only)
        }, 2000);
      }
    } catch (err) {
      setShowToast({ message: err.message, severity: "error" });
      Sentry.captureException(err);
    } finally {
      setIsLoading(false);
    }
  };

  const Style = StyleSheet.create({
    mainBoxStyle: {
      display: "flex",
      gap: "30px",
      "@media (max-width: 768px)": {
        flexDirection: "column",
        padding: "0px 24px",
      },
    },
  });

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>

      <Grid container>
        <Formik
          initialValues={{ ...initialOrganizationValue }}
          enableReinitialize // ✅ optional but recommended if user loads after refresh
          onSubmit={debounce((values) => {
            if (isLoading || isSubmitLocked) return;

            const result = handleFriendsValidation(NEW_FRIEND_FIELDS, values);
            if (result && Object.keys(result).length) {
              setShowToast(result);
              return;
            }

            // // ✅ ADDRESS STRICT FORMAT VALIDATION
            // const STRICT_ADDRESS_REGEX =
            //   /^[A-Za-z0-9\s\-/,]+,\s?[A-Za-z\s]+,\s?[A-Za-z\s]+ - \d{6}$/;

            // if (
            //   !STRICT_ADDRESS_REGEX.test(
            //     values[NEW_FRIEND_FIELDS.ADDRESS_LINE_1] || ""
            //   )
            // ) {
            //   setShowToast({
            //     message:
            //       "Address format invalid. Use: Flat No, Building Name, Area - 400101",
            //     severity: "error",
            //   });
            //   return;
            // }

            postData(values);
          })}
        >
          {({ values, errors, handleChange, handleSubmit, setFieldValue }) => (
            <>
              <Box className={css(Style.mainBoxStyle)}>
                <Grid item md={8} sm={12}>
                  <NewFriendForm
                    values={values}
                    handleChange={handleChange}
                    errors={errors}
                    setFieldValue={setFieldValue}
                  />
                </Grid>

                <Grid item md={4} sm={12}>
                  {positionRole === "Region" ? (
                    <OrganizationStructureRegion
                      values={values}
                      handleChange={handleChange}
                      errors={errors}
                      handleSubmit={handleSubmit}
                      setFieldValue={setFieldValue}
                      isLoading={isLoading}
                      showZoneLocator={false}
                      showBlock
                      disabledOSKeys={OS_KEYS}
                    />
                  ) : (
                    <OrganizationStructure
                      values={values}
                      handleChange={handleChange}
                      errors={errors}
                      handleSubmit={handleSubmit}
                      setFieldValue={setFieldValue}
                      isLoading={isLoading}
                      showZoneLocator={false}
                      showBlock
                      disabledOSKeys={OS_KEYS}
                    />
                  )}
                </Grid>
              </Box>

              <Divider
                sx={{
                  display: "flex",
                  width: "100%",
                  paddingTop: "1%",
                  marginBottom: "30px",
                }}
              />

              {isMobile ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    paddingLeft: "24px",
                    minHeight: "190px",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    isLoading={isLoading}
                    label={isSubmitLocked ? "Submitted" : "Submit"}
                    type="submit"
                    disabled={isLoading || isSubmitLocked}
                  />

                  <Button
                    variant="outlined"
                    onClick={onBack}
                    label="Cancel"
                    disabled={isLoading || isSubmitLocked}
                    style={{ marginLeft: "20px" }}
                  />
                </Box>
              ) : (
                <Grid item>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    isLoading={isLoading}
                    label={isSubmitLocked ? "Submitted" : "Submit"}
                    type="submit"
                    disabled={isLoading || isSubmitLocked}
                  />
                  <Button
                    variant="outlined"
                    onClick={onBack}
                    label="Cancel"
                    style={{ marginLeft: "20px" }}
                  />
                </Grid>
              )}
            </>
          )}
        </Formik>
      </Grid>

      <Toast
        open={!!showToast}
        severity={showToast?.severity || ""}
        message={showToast?.message || ""}
        handleClose={() => setShowToast(null)}
      />
    </>
  );
}

export default FriendForm;
